// What the results sheet has to show, in types — and the words it says.
//
// Built ON @cca/decides-count's own result types (type-only, as everywhere in
// this package), so a rule that moves in the engine moves here. What is added is
// only what the ARITHMETIC has no opinion about: the words of the motion, the
// candidates' display names, whether the ballot was secret, and where the count
// came from.
//
// One rule governs the whole file, and it is ratified, not a preference —
// migrations/0001_initial.sql:500, "SO 14.1(k): the President declares. The
// system reports; it never declares." So nothing here is phrased as a
// declaration, there is no hero verdict band, and the largest thing on the
// screen is the arithmetic.

import type {
  BallotMode,
  CountIssue,
  CountResult,
  House,
  IssueCode,
  MotionItemResult,
  MotionScopeResult,
  PositionItemResult,
  TieReport,
} from '@cca/decides-count';
import type { RollIntegrity } from '../api/client';
import { HOUSE_LABEL } from '../paper';

/**
 * Whether the papers may be attributed to the member who cast them —
 * `ballot.secrecy` in migrations/0001_initial.sql:233. Election ballots are
 * structurally always `secret` (SO 15.9, enforced by the
 * `ballot_elections_are_always_secret` CHECK at :238); a MOTION ballot may be
 * either.
 *
 * **Nothing on the sheet renders this today, and that is not an oversight.** Its
 * one reader was the Bishop's line beneath the tally, which gated whether his
 * choice could be NAMED — and registrar ruling 2026-08-16 dissolved the
 * one-person tally that line reported rather than suppressing its display (the
 * account is in MotionResult.tsx, where the line used to be). The field stays
 * because it is the ballot's own state and the assembler must carry it
 * truthfully, not as a hook waiting for a component: a screen that names how one
 * identified person voted does not get built back on the strength of an unused
 * field.
 */
export type BallotSecrecy = 'secret' | 'attributable';

/**
 * The ballot states a results sheet can be rendered for. The schema's state
 * column also carries `draft`, `ready` and `open`, and this screen deliberately
 * cannot be built for any of them: a visible tally during an open secret ballot
 * changes votes (the same rule that keeps every tally off BallotPaper).
 *
 * **This type is not the gate, because a type in a browser never is.** The
 * schema's own CHECK admits all six states, so the same three are held again
 * server-side by `REPORTABLE_STATES` in `@cca/decides-worker`'s
 * `src/routes/results.ts`: a route that trusts the client's notion of "closed"
 * is trusting the client.
 */
export type ResultBallotState = 'closed' | 'sealed' | 'declared';

/**
 * Who is looking — and **there is no member in this union, deliberately.**
 *
 * Registrar ruling, 2026-08-16, verbatim: _"no numbers are declared on the
 * ballot or vote ever. It is secret. … Nothing that discloses vote numbers,
 * percentages or results should ever be made known to the citizenry during or
 * after the vote. It should be collected and held by the Admin and
 * Scrutineers."_ Confirmed on follow-up: there is **no member-facing results
 * screen at all**, at any distance — not during the count, not after the
 * declaration, not in an archive months later. The President declares the
 * outcome to Synod in the room, having checked with the scrutineers, exactly as
 * a manual division works.
 *
 * So this is not a reading level, it is the whole audience of the page, and a
 * `'member'` arm is not a branch worth keeping "just in case": a union member
 * that must never be served is an invitation to serve it. If one is ever added
 * back, the thing that has changed is the ruling, and the ruling is what has to
 * change first.
 *
 * **These are the ruling's own two words, and they are the SERVER'S two words
 * too** — `GrantedRole` in `@cca/decides-worker`'s `src/grants.ts` and
 * `role_grant.role`'s CHECK in `migrations/0005_grants.sql` are the same pair,
 * spelt the same way. The arm used to be called `'registrar'` while the ruling
 * said Admin and the grant table had not been built; one word now means one
 * thing from the CHECK constraint to the heading on the screen.
 *
 * The two are not interchangeable. A scrutineer may see every figure on the
 * sheet and is still not the Admin, so the Admin's own block stays keyed on
 * `viewer === 'admin'` (ResultPage) — the audience of the page and the authority
 * to act on it are two questions, asked separately.
 *
 * **This type does not enforce anything, and it is no longer the only thing
 * standing between the sheet and a reader**: `GET /api/results/:ballotId`
 * resolves the viewer from a session, a `Contacts.id` and a grant row, and
 * refuses everybody else. This union is what the page is TOLD; that route is
 * what decides.
 */
export type Viewer = 'admin' | 'scrutineer';

/** A motion or canon, as reported. */
export interface MotionResultView {
  kind: 'motion';
  result: MotionItemResult;
  title: string;
  /** The instrument — the words Synod agreed or refused, verbatim and entire,
   *  exactly as on the paper. A result nobody can read the motion of is not a
   *  report of anything, so this one is REQUIRED: a motion whose instrument did
   *  not come back has no sheet at all rather than a sheet of figures under a
   *  title. Split into paragraphs at the render, by the paper's own
   *  `instrumentParagraphs` (../paper), and never as markup. */
  instrument: string;
  /**
   * Who moved the motion, and who seconded it, as the record holds them — and
   * **`null` where it does not**, which is a lawful motion and not a gap
   * (`ballot_item.mover` / `.seconder` are NULLable; migration 0016).
   *
   * An absent name draws NO row: not `''`, not a dash, not "not recorded". A
   * results sheet is the record of what Synod agreed or refused, and a printed
   * attribution nobody made is a falsehood in the one place that must not carry
   * one. Same rule and same direction as `MarkableMotion` (../paper), so the
   * paper the member marked and the report of it say the same thing.
   */
  mover: string | null;
  seconder: string | null;
}

/** An election for one office or body, as reported. */
export interface PositionResultView {
  kind: 'position';
  result: PositionItemResult;
  title: string;
  /** Display names keyed by candidate id. Every rule in the engine keys on the
   *  ID; this map is for the reader's eyes only, and a missing entry falls back
   *  to the id rather than rendering an empty cell. */
  candidateNames: Record<string, string>;
}

export type ResultItemView = MotionResultView | PositionResultView;

/** Where this count came from — the scrutineer's trail, in the disclosure. */
export interface ProvenanceView {
  countRunId: string;
  engineVersion: number;
  definitionDigest: string;
  resultDigest: string;
  /**
   * The run time already rendered in NZ words (Pacific/Auckland). Formatted by
   * whoever assembles the view, never here: a component that formats an instant
   * has to pick a zone, and the one that is right is never the browser's.
   */
  runAtNz: string;
  /**
   * The frozen roll's fingerprint — the third digest of the scrutineer's trail,
   * beside the definition and the result, because "against this roll" is half
   * the sentence a signature is. `null` means NO FROZEN ROLL (a rehearsal before
   * the freeze), never zero, and it draws its own plain words rather than an
   * empty mono cell.
   */
  rollDigest: string | null;
  /** The amendment sequence the roll was read at for this count — the index the
   *  summoned figures in {@link CoverageView} are the effect of. `null` exactly
   *  when `rollDigest` is. */
  rollAmendmentSeq: number | null;
}

/**
 * One SO 15.5 extension as the sheet says it: the instant the close was moved,
 * already in NZ words, and the chain's stored `reason` sentence VERBATIM. The
 * sentence is prose an operator's act wrote (`close extended from … to …`) and
 * is rendered whole as text, never parsed and never as markup.
 */
export interface ExtensionView {
  occurredAtNz: string;
  reason: string | null;
}

/**
 * WHAT THE SIGNATURE COVERS — the box, the roll and the window. A scrutineer's
 * signature attests "I ran this count, on this box of papers, against this
 * roll, and the figures here are what it produced", and until the sheet stated
 * all three the sentence was checkable against nothing (the count route's own
 * header carries the account). Times arrive already in NZ words, formatted by
 * whoever assembles the view — the same rule as {@link ProvenanceView.runAtNz}.
 *
 * `extensions` is ALWAYS an array: `[]` means "the poll ran to its published
 * close", never absence, so an extended poll and an unextended one cannot render
 * down one branch — a signed sheet that hides an extension is the exact harm
 * this block exists to close, which is why the field is required here and on
 * {@link ResultView}.
 */
export interface CoverageView {
  /** A COUNT of papers, never a paper; 0 is a lawful seal of an empty box. */
  papersSealedCount: number | null;
  papersSealedAtNz: string | null;
  /** Null on all three together means NO FROZEN ROLL, never zero. */
  summonedClergy: number | null;
  summonedLaity: number | null;
  summonedBishop: number | null;
  /** The published close AS IT FINALLY STOOD, in NZ wall-clock words; null is
   *  a `manual` ballot with no published closing time, not a gap. */
  closesAtNz: string | null;
  extensions: ExtensionView[];
}

/**
 * THE MARKS-ROLL CHECK as the sheet says it — three answers, never two
 * (cca-71hp's flag, cca-5uma's reading of it).
 *
 *  • `clean`     — the check ran and the record of who voted names nobody the roll
 *                  says may not vote. The sheet says NOTHING for it: this is the
 *                  ordinary case, and a line on every sheet would teach the reader
 *                  to skip the line on the one sheet that matters.
 *  • `found`     — the check ran and found `ineligibleOnRoll` (> 0) such people.
 *  • `unchecked` — the check did not complete, or the worker could not say it did.
 *                  NOT clean, and the sheet says so in words, because silence here
 *                  is exactly what a reader would fill in with "clean".
 *
 * Built from the wire by {@link rollIntegrityFrom}, directly below — the ONE
 * reading of the wire, exported so a second assembler imports it rather than
 * writing its own.
 */
export type RollIntegrityView =
  { state: 'clean' } | { state: 'found'; ineligibleOnRoll: number } | { state: 'unchecked' };

/**
 * The count route's marks-roll check → what the sheet says about it
 * ({@link RollIntegrityView}). ONE RULE: only a check that says it RAN, and
 * counted zero, is clean (cca-5uma).
 *
 *  • A positive count is a finding whatever else came with it — a failed check
 *    answers `null`, never a number, so nothing invents one.
 *  • `checked` that is the boolean `true` — `=== true`, never truthiness, so a
 *    string `"false"` is not a yes — with a count of exactly zero is the clean
 *    answer, and the only road to it.
 *  • EVERYTHING ELSE is not checked: the worker's `checked: false`, and also a
 *    body with no `checked` at all — a worker older than this bundle, whose zero
 *    may be a failure it could not tell apart from a clean roll (the defect this
 *    replaced) — and a body whose `rollIntegrity` is absent or `null`. Each of
 *    those says "could not be checked" on the sheet, never nothing.
 *
 * HERE, BESIDE THE VIEW IT BUILDS, and exported (cca-5uma F7): a reading kept
 * private to one screen is a reading the next screen copies, and a copy is where
 * the two start disagreeing about what "clean" means.
 *
 * `null` and `undefined` are in the parameter, though not in the wire type,
 * because this reads a response body and a body can lack what its type
 * promises; the type describes what the worker sends, this describes what the
 * screen will believe. `== null` takes both, so neither reaches the property
 * read below — which would throw mid-render, with no error boundary to catch it,
 * and leave the counted screen undrawn.
 */
export function rollIntegrityFrom(wire: RollIntegrity | null | undefined): RollIntegrityView {
  if (wire == null) return { state: 'unchecked' };
  const count = wire.ineligibleOnRoll;
  if (typeof count === 'number' && count > 0) return { state: 'found', ineligibleOnRoll: count };
  if (wire.checked === true && count === 0) return { state: 'clean' };
  return { state: 'unchecked' };
}

/**
 * Everything on the screen: one counted ballot.
 *
 * **`destruction_authorised_at_utc` (SO 8.9) is deliberately absent, and its
 * absence is the control.** Authorising destruction of the papers is an explicit
 * recorded act of Synod; it is not reachable from a reporting screen, so it is
 * not in this type, not in a prop, and not behind a registrar flag. A field that
 * does not exist cannot be rendered by the next person in a hurry.
 */
export interface ResultView {
  /** The count, verbatim from the engine. Never edited on the way in — in
   *  particular `carried: null` must arrive as an explicit null (see
   *  MotionScopeResult.carried: a transport that strips nulls turns a withheld
   *  decision into "not carried"). */
  result: CountResult;
  ballot: {
    title: string;
    state: ResultBallotState;
    secrecy: BallotSecrecy;
  };
  /** The items in AGENDA order. `CountResult.items` is ordered by item id so the
   *  result digest is reproducible; display order comes from the definition. */
  items: ResultItemView[];
  provenance: ProvenanceView;
  /**
   * The box, the roll and the window — see {@link CoverageView}. REQUIRED, and
   * deliberately not optional: an assembler that could omit it could render a
   * signed sheet that says nothing about the poll being held open, and that
   * omission is the one that makes a signature actively misleading rather than
   * merely incomplete.
   */
  coverage: CoverageView;
  /**
   * The marks-roll check — see {@link RollIntegrityView}. REQUIRED, for the
   * same reason `coverage` is: the clean answer renders nothing, so an assembler
   * that could OMIT this field would draw exactly the sheet a clean check draws,
   * and an unasked question would read as a clean answer. Nothing defaults it.
   */
  rollIntegrity: RollIntegrityView;
  /**
   * Who the SERVER decided is reading. **Required, and there is no default
   * anywhere** — the render site used to fall back to `'scrutineer'`, and a
   * default of any kind is the browser deciding the reader, which is precisely
   * the defect this field's server-side twin exists to remove. An absent viewer
   * is not a least-privileged reader; it is a page nobody has been authorised to
   * see, and it must be impossible to assemble a `ResultView` without saying who
   * asked for it.
   *
   * It decides one thing on the screen — whether the Admin's own block renders —
   * because everyone in {@link Viewer} may see every figure on the sheet and
   * nobody outside it may see the sheet at all. The second half is enforced at
   * `GET /api/results/:ballotId`, never here.
   */
  viewer: Viewer;
}

/**
 * How each aggregation scope is named to a reader. The two Houses reuse the
 * ballot paper's own labels; `all` is the one-tally case under SO 8.4.
 *
 * **There is no third entry and there never is one.** The Bishop sits in neither
 * House (registrar ruling 2026-08-15) — he is not a "House of Bishops" and he is
 * not a column. Nor is he a line beneath the table any more (ruling 2026-08-16):
 * his paper is inside the clergy figures where the base is the votes themselves,
 * and otherwise excluded from every tally and reported as an issue.
 */
const SCOPE_LABEL: Record<House | 'all', string> = {
  ...HOUSE_LABEL,
  all: 'Synod',
};

/**
 * The same scope as a COLUMN HEADING, and deliberately shorter.
 *
 * "House of Clergy" is a 15-character label sitting above a two-digit number, in
 * a three-column table that has to fit 320px without a horizontal scroll (WCAG
 * 2.2 SC 1.4.10 Reflow). At 390px it did not: the header set the column's
 * min-content width and pushed "House of Laity" off the right edge. Nothing is
 * lost by shortening it — the caption above the table says "in each House", and
 * the verdict sentence beneath it names both Houses in full. The full names stay
 * in {@link SCOPE_LABEL}, which is what a row header and a sentence use.
 */
export const SCOPE_COLUMN_LABEL: Record<House | 'all', string> = {
  clergy: 'Clergy',
  laity: 'Laity',
  all: 'Synod',
};

/** A scope named inside a sentence: "the House of Laity", "Synod". The article
 *  belongs to the House and not to Synod, and getting it from a lookup keeps the
 *  verdict sentence and the withheld card saying the same words. */
export function scopePhrase(scope: House | 'all'): string {
  return scope === 'all' ? 'Synod' : `the ${SCOPE_LABEL[scope]}`;
}

/** "the House of Laity voted 31 for and 18 against" — the clause both the
 *  verdict sentence and the withheld card are built from. Deliberately stops
 *  before any tail, because the tails differ and only one of them is a verdict. */
export function votesPhrase(scope: MotionScopeResult): string {
  return `${scopePhrase(scope.scope)} voted ${String(scope.ayes)} for and ${String(scope.noes)} against`;
}

// ── What the signature covers, in sentences ────────────────────────────────
// Three statements and one list: the box, the roll, the window, and the acts
// that moved the window. Each is a plain sentence rather than a table because
// they are read out and countersigned, not compared — and each has an honest
// words-for-absence branch, never a silent omission, because absence here is
// exactly what a reader would fill in with the reassuring assumption.

/** THE BOX — "104 papers were sealed at 8:31pm…". A lawful seal of zero papers
 *  says "0 papers were sealed", which is the instant witnessing that the box
 *  shut, not a defect. The absent branch is unreachable off a real count (the
 *  route refuses unsealed states) and is worded for the deploy-skew case. */
export function boxPhrase(coverage: CoverageView): string {
  if (coverage.papersSealedCount === null || coverage.papersSealedAtNz === null) {
    return 'The sealing of the papers is not recorded on this sheet.';
  }
  return `${plural(coverage.papersSealedCount, 'paper was', 'papers were')} sealed at ${coverage.papersSealedAtNz}.`;
}

/** THE ROLL — the summoned figure per House the count divided by. Worded as a
 *  fact about the ROLL, not about the count, so it stays true on a fatal count
 *  that never ran. The Bishop is summoned on his own, exactly as
 *  `summoned_bishop` is — he is not a third House and gets no House's name. */
export function rollPhrase(coverage: CoverageView): string {
  const { summonedClergy, summonedLaity, summonedBishop } = coverage;
  if (summonedClergy === null || summonedLaity === null || summonedBishop === null) {
    return 'No frozen roll stands behind this count — this sitting’s roll was never frozen.';
  }
  const bishop = summonedBishop === 0 ? 'no Bishop' : plural(summonedBishop, 'Bishop', 'Bishops');
  return (
    `The frozen roll summoned ${plural(summonedClergy, 'member', 'members')} in the ` +
    `${HOUSE_LABEL.clergy}, ${String(summonedLaity)} in the ${HOUSE_LABEL.laity}, and ${bishop}.`
  );
}

/**
 * THE WINDOW — the close as it finally stood. With no extensions the second
 * sentence says the poll ran to its published close, which is the wire's own
 * meaning for an empty `extensions` array; with extensions the caller renders
 * {@link movedPhrase} and the moves themselves IMMEDIATELY after, so the final
 * close is never printed as if it had always been the close. The null branch is
 * a `manual` ballot: no closing time was ever published, so neither tail fits.
 */
export function windowPhrase(coverage: CoverageView): string {
  if (coverage.closesAtNz === null) {
    return 'No closing time was published for this ballot: voting stayed open until a Scrutineer closed it.';
  }
  return coverage.extensions.length === 0
    ? `Voting closed at ${coverage.closesAtNz}. The poll ran to its published closing time.`
    : `Voting closed at ${coverage.closesAtNz}.`;
}

/** "The closing time was moved 2 times:" — the plain statement the registrar's
 *  scope requires whenever `extensions` is non-empty, introducing each move's
 *  stored sentence verbatim. */
export function movedPhrase(count: number): string {
  return `The closing time was moved ${plural(count, 'time', 'times')}:`;
}

// ── The marks-roll check, in sentences ─────────────────────────────────────
// Two notices and no third — `clean` says nothing (see RollIntegrityView). Each
// states the FACT and WHO TO TELL, and nothing about what the fact means for the
// result: whether a count stands with such a name on its record is not this
// screen's to say, and no ruling on it exists to quote. The person to tell is the
// registrar, named with the estate's own address.
//
// THE STATE COMES FIRST, BOLD, AFTER ONE LABEL (Miranda, cca-5uma r1). The two
// notices used to share their first eight words, and on a black-and-white printed
// sheet the words are all there is — so each now opens "Roll check:" and then its
// state: the head-count, or "not completed". Only the label and that state are
// bold; the sentence around them is plain.
//
// "HAVING NO VOTE", NOT "NOT ENTITLED TO VOTE" (Miranda, cca-5uma r2). The sheet
// already says "not entitled to vote" in a position's tally — "Excluded — not
// entitled to vote here" — and that row counts something DIFFERENT: papers from
// a House that may not vote for THIS position under SO 15.8. This notice counts
// people the roll says may not vote AT ALL (`may_vote = 0`). Two counts under one
// phrase invite a reader to add them or to take one for the other, so this one is
// said in the registrar's own framing — "no ability to vote" — as "having no
// vote". It does NOT point at the tally row: a motion sheet has no such row.
//
// "Officer" is the example because it is the registrar's own (2026-08-14,
// verbatim: "Officer has no ability to vote, only administer"); the check itself
// is wider — anybody the roll records as having no vote — so the Officer is an
// example and never the definition. It is said as a FACT ABOUT THE ROLL, apart
// from the finding: inside the finding's own sentence "1 person … such as an
// Officer" reads close to "an Officer voted", which nothing here knows. In the
// not-completed notice it stays inline, where it is plainly hypothetical.
//
// The operator panel's pointer calls the notice "the roll check", because that is
// how the notice opens — the words a person hears there are the words they land on.

/** The `id` the notice carries, so the operator panel's pointer line can send a
 *  screen-reader scrutineer straight to it (see `RollIntegrityNotice`). There is
 *  at most one notice on a page. */
export const ROLL_INTEGRITY_NOTICE_ID = 'result-roll-integrity-note';

/**
 * The `found` notice's lead, in its two parts — `count` is bold on the sheet and
 * `rest` is not: "**2 people** the roll records as having no vote are named on
 * the record of who voted on this ballot." People, not marks: the record holds
 * one row per person per ballot (its primary key), so the count IS a head-count.
 */
export function rollIntegrityFoundLead(ineligibleOnRoll: number): { count: string; rest: string } {
  return {
    count: plural(ineligibleOnRoll, 'person', 'people'),
    rest: `the roll records as having no vote ${ineligibleOnRoll === 1 ? 'is' : 'are'} named on the record of who voted on this ballot.`,
  };
}

/** The fixed words of the two notices and of the operator panel's pointer to
 *  them. `uncheckedRest` says plainly that it is NOT the clean answer, because
 *  the clean answer is silence and silence is what a reader would otherwise fill
 *  in. `foundFollow` and `uncheckedRest` each end where the registrar's address
 *  follows on the sheet. The pointer carries no number, nothing about the
 *  result, and no instruction about signing — it only says where the roll check
 *  is. */
export const ROLL_INTEGRITY_COPY = {
  label: 'Roll check:',
  foundFollow:
    'This sheet does not name them. (The roll records an Officer, for example, as having no vote.) Tell the registrar:',
  uncheckedState: 'not completed.',
  uncheckedRest:
    'The record of who voted on this ballot could not be checked against the roll, so this sheet cannot say whether anybody with no vote — such as an Officer — is recorded as having voted. That is not the same as finding nobody. Ask the registrar to check it:',
  pointer: 'The sheet above reports a roll check on the record of who voted.',
  pointerLink: 'Go to the roll check',
} as const;

/** The state line under the ballot title. Each one ends by saying who declares,
 *  because this screen never does. */
export const RESULT_STATE_COPY: Record<ResultBallotState, string> = {
  closed:
    'Voting has closed and the papers have been counted. Nothing on this page has been declared: under Standing Order 14.1(k) the President declares the result to Synod.',
  sealed:
    'Voting has closed, the papers have been counted and the count is sealed. Nothing on this page has been declared: under Standing Order 14.1(k) the President declares the result to Synod.',
  declared:
    'The President has declared this ballot to Synod. This page reports the count the declaration was made from.',
};

/** The band that says this is not a live ballot. Keyed on the engine's own mode
 *  union minus `live`, so a fourth mode reds this file rather than rendering a
 *  practice ballot with no band at all. */
export const MODE_BAND_COPY: Record<
  Exclude<BallotMode, 'live'>,
  { label: string; sentence: string }
> = {
  rehearsal: {
    label: 'Rehearsal',
    sentence:
      'This is a rehearsal. Nothing on this page is a vote of Synod, and nothing here may be declared.',
  },
  sandbox: {
    label: 'Sandbox',
    sentence:
      'This is a practice ballot on invented data. Nothing on this page is a vote of Synod, and nothing here may be declared.',
  },
};

/**
 * Every issue code the engine can emit, in a sentence a person can read at 9pm
 * with a room waiting. Keyed on the engine's closed union so the compiler reds
 * this file the day a code is added upstream — an unmapped code would otherwise
 * render as nothing at all, which is the failure that looks fine.
 *
 * Plain words, and the reader is a scrutineer or the Admin (see
 * {@link Viewer}), not a member: the engine's own `message` says things like
 * "(carried: null)" and names the fields it read. It is kept verbatim, and shown
 * verbatim, in the provenance disclosure — one disclosure away, in front of
 * nobody who did not ask.
 */
export const ISSUE_COPY: Record<IssueCode, string> = {
  // fatal — the count did not run
  'definition-shape': 'The ballot was not the shape the engine can count, so no count was run.',
  'definition-version':
    'The ballot was written for a different version of the counting engine, so no count was run.',
  'duplicate-item-id': 'Two items on this ballot share one id, so no count was run.',
  'duplicate-candidate-id': 'Two candidates share one id, so no count was run.',
  'invalid-seat-count':
    'A position declared a number of places the engine cannot count against, so no count was run.',
  'invalid-threshold':
    'A motion declared a majority the engine cannot count against, so no count was run.',
  'invalid-roll': 'The roll of members was not usable, so no count was run.',
  'roll-missing-all-members':
    'This ballot is counted against the standing roll of every member of Synod, and that roll was not supplied, so no count was run.',
  'zero-roll-base':
    'A House was recorded as summoning nobody. There is no denominator to count against, so no count was run.',
  'by-houses-required':
    'This motion must be counted by Houses and was not set up that way, so no count was run.',
  'method-not-implemented':
    'The counting method this position asks for is not built. Nothing may be declared for it.',
  'unknown-method':
    'The counting method named for a position is not one the engine knows, so no count was run.',
  'invalid-tie-break': 'A recorded tie resolution was not usable, so no count was run.',
  'vote-shape': 'A paper was not the shape the engine can count, so no count was run.',
  'unknown-item': 'A paper was marked for an item that is not on this ballot, so no count was run.',
  'unknown-candidate':
    'A paper was marked for someone who is not standing for this position, so no count was run.',
  'vote-kind-mismatch':
    'A paper of one kind was counted against an item of the other kind, so no count was run.',
  // non-fatal — the count ran and reported these
  'ineligible-elector':
    'Papers arrived from members whose House may not vote for this position. They were excluded from the count, and they are reported here rather than dropped quietly (SO 15.8).',
  // The sibling of the line above, and deliberately NOT worded like it. An
  // ineligible mark is a paper somebody had no right to cast; this is a lawful
  // elector's lawful paper that the Constitution gives no House to land in, and
  // a returning officer acts on the two differently. So: no "excluded" without
  // the reason beside it, nothing that reads as a defect in the ballot, and the
  // consequence said plainly — he has to be told before anything is declared.
  'bishop-vote-excluded':
    'The Bishop marked a paper and it is not counted in this division. This item is decided by Houses against the roll, and the roll seats the Bishop on his own rather than in a House (§15.2 / §18.1), so there is no House column his paper can enter. It is recorded and reported rather than quietly dropped — nothing is wrong with the ballot, and the Bishop should be told before anything is declared.',
  'over-vote':
    'One or more papers marked more candidates than there are places, and were not counted.',
  'duplicate-mark':
    'One or more papers marked the same candidate more than once. The repeat was counted once; the paper was not rejected.',
  'votes-exceed-roll':
    'More papers were counted than the roll they are counted against contains. No decision is reported for this item until a person has settled which of the two is wrong.',
  'roll-contradicts-summons':
    'The standing roll of Synod is short of the summons list for this sitting. Both can be correct — a member may have died, resigned or had a licence lapse since the summons went out — so a person confirms which it was before anything is declared.',
  'present-exceeds-summons':
    'More members were present than were summoned. That is lawful — a member may vote who was not summoned — and it is also what a short summons list looks like, so a person confirms which it was before anything is declared.',
  'tie-unresolved':
    'A tie at a cut line has no recorded resolution. Standing Order 15.13 requires a fresh vote among the tied candidates, and failing that a draw by the Registrar, before anything may be declared.',
  'tie-break-unused':
    'A recorded tie resolution does not bind any tie that arose in this count, so it was not applied.',
  'no-confidence-carried':
    'No Confidence outpolled one or more candidates. Those places are unfilled and go to Standing Committee (SO 15.7, SO 15.12); the next-highest candidate is not elected.',
  'insufficient-candidates':
    'Fewer candidates stood than there are places. The shortfall goes to Standing Committee to appoint (SO 15.12).',
  'threshold-mismatch':
    'The majority set for this motion differs from the constitutional default for its class. The registrar sets it per item; the difference is recorded here so it is visible.',
  'motion-class-absent':
    'No class was recorded for this motion, so the engine could not check the majority set for it against the constitutional default.',
  'quorum-not-met': 'Quorum was not met — half the members summoned in each House (SO 12.1).',
};

/** How each severity is introduced. `fatal` and `error` say what has to happen;
 *  `warning` says only that it is on the record. */
export const SEVERITY_LABEL: Record<CountIssue['severity'], string> = {
  fatal: 'The count did not run',
  error: 'Needs a person before anything is declared',
  warning: 'Recorded',
};

/** The issues naming one item. */
export function issuesForItem(result: CountResult, itemId: string): CountIssue[] {
  return result.issues.filter((issue) => issue.itemId === itemId);
}

/** The issues that name no item — facts about the ROLL or the sitting, equally
 *  true of every item on the paper (see validate.ts:518). */
export function ballotIssues(result: CountResult): CountIssue[] {
  return result.issues.filter((issue) => issue.itemId === undefined);
}

/** Whether the engine reported `code` against this item. Reads the engine's own
 *  report rather than re-deriving the finding — the second copy of a rule is
 *  where the screen and the count start disagreeing. */
export function itemHasIssue(result: CountResult, itemId: string, code: IssueCode): boolean {
  return result.issues.some((issue) => issue.itemId === itemId && issue.code === code);
}

/**
 * The scopes whose decision is WITHHELD — `carried === null`, meaning the roll
 * snapshot and the vote set disagree and the engine declines to publish a
 * decision derived from the pair.
 *
 * Written as `=== null` and never as `!scope.carried`: `false` says Synod
 * refused the motion and `null` says nobody may declare it either way yet, and
 * the falsy test folds the second into the first.
 */
export function withheldScopes(result: MotionItemResult): MotionScopeResult[] {
  return result.scopes.filter((scope) => scope.carried === null);
}

/** The scopes Synod definitively refused. Again `=== false`, not falsy. */
export function refusedScopes(result: MotionItemResult): MotionScopeResult[] {
  return result.scopes.filter((scope) => scope.carried === false);
}

/** The ties with no recorded resolution (SO 15.13). */
export function unresolvedTies(result: PositionItemResult): TieReport[] {
  return result.ties.filter((tie) => !tie.resolved);
}

/**
 * `n` and its noun, pluralised. Small, and here rather than inline, because
 * "1 papers" in a sentence about a contradicted roll is exactly the kind of
 * sloppiness that makes a reader distrust the numbers beside it.
 */
export function plural(n: number, one: string, many: string): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}
